import React, { useState, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, FileText, DollarSign, Building2, Target } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import TenderForm from './TenderForm';
import TenderDetail from './TenderDetail';
import ApplicationForm from './ApplicationForm';
import { Tender, Application } from '../types';

interface TenderCalendarProps {
  onPageChange?: (page: string) => void;
}

const TenderCalendar: React.FC<TenderCalendarProps> = ({ onPageChange }) => {
  const { tenders, organizations, applicationStatuses, applications } = useData();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showTenderDetail, setShowTenderDetail] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Obtener el primer y último día del mes actual
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const firstDayOfWeek = firstDayOfMonth.getDay();

  // Generar días del calendario
  const calendarDays = useMemo(() => {
    const days = [];
    
    // Días del mes anterior para completar la primera semana
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(firstDayOfMonth);
      date.setDate(date.getDate() - i - 1);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Días del mes actual
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Días del mes siguiente para completar la última semana
    const remainingDays = 42 - days.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate, firstDayOfMonth, lastDayOfMonth, firstDayOfWeek]);

  // Obtener licitaciones filtradas por fecha y estado
  const getFilteredTendersForDate = (date: Date) => {
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth();
    const targetDay = date.getDate();
    
    return tenders.filter(tender => {
      const closingDate = new Date(tender.closing_date);
      const closingYear = closingDate.getFullYear();
      const closingMonth = closingDate.getMonth();
      const closingDay = closingDate.getDate();
      
      return closingYear === targetYear && 
             closingMonth === targetMonth && 
             closingDay === targetDay &&
             (tender.flujo === 'Disponible' || tender.flujo === 'En Revision');
    });
  };

  // Obtener postulaciones filtradas por fecha y estado
  const getFilteredApplicationsForDate = (date: Date) => {
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth();
    const targetDay = date.getDate();
    
    return applications.filter(application => {
      const tender = tenders.find(t => t.id === application.tender_id);
      if (!tender) return false;
      
      const closingDate = new Date(tender.closing_date);
      const closingYear = closingDate.getFullYear();
      const closingMonth = closingDate.getMonth();
      const closingDay = closingDate.getDate();
      
      const status = applicationStatuses.find(s => s.id === application.status_id);
      return closingYear === targetYear && 
             closingMonth === targetMonth && 
             closingDay === targetDay &&
             (status?.name === 'En Revision' || status?.name === 'Ofertada');
    });
  };

  // Navegación del calendario
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${currency} ${amount.toLocaleString()}`;
  };

  const getOrganizationName = (organizationId: string) => {
    return organizations.find(org => org.id === organizationId)?.name || 'Organización no encontrada';
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleTenderClick = (tender: Tender) => {
    // Verificar si la licitación está en estado final (no editable) usando el status_id de la licitación
    const applicationStatus = applicationStatuses.find(s => s.id === tender.application_status_id);
    const isNonEditable = applicationStatus?.name === 'Adjudicada' || applicationStatus?.name === 'Descartada';
    
    setSelectedTender(tender);
    setIsReadOnly(isNonEditable);
    setShowTenderDetail(true);
  };

  const handleApplicationClick = (application: Application) => {
    setSelectedApplication(application);
    setShowApplicationForm(true);
  };

  const handleCloseTenderDetail = () => {
    setShowTenderDetail(false);
    setSelectedTender(null);
    setIsReadOnly(false);
  };

  const handleCloseApplicationForm = () => {
    setShowApplicationForm(false);
    setSelectedApplication(null);
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Función para obtener el color según el tipo y estado
  const getEventColor = (type: 'tender' | 'application', status: string) => {
    if (type === 'tender') {
      switch (status) {
        case 'Disponible':
          return '#0ea5e9'; // Celeste
        case 'En Revision':
          return '#a855f7'; // Lila
        case 'Desconocido':
        default:
          return '#6b7280';
      }
    } else {
      switch (status) {
        case 'En Revision':
          return '#2563eb'; // Azul
        case 'Ofertada':
          return '#ea580c'; // Naranja
        case 'Desconocido':
        default:
          return '#6b7280';
      }
    }
  };

  const getTenderInfo = (tenderId: string) => {
    return tenders.find(tender => tender.id === tenderId);
  };

  if (showTenderDetail && selectedTender) {
    if (isReadOnly) {
      return (
        <TenderDetail
          tender={selectedTender}
          onClose={handleCloseTenderDetail}
        />
      );
    } else {
      return (
        <TenderForm
          tender={selectedTender}
          onClose={handleCloseTenderDetail}
        />
      );
    }
  }

  if (showApplicationForm && selectedApplication) {
    return (
      <ApplicationForm
        application={selectedApplication}
        onClose={handleCloseApplicationForm}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendario de Licitaciones y Postulaciones</h1>
          <p className="text-gray-600">Fechas de cierre de licitaciones y envío de postulaciones</p>
        </div>
        <button
          onClick={goToToday}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
        >
          <Calendar className="h-5 w-5 mr-2" />
          Hoy
        </button>
      </div>

      {/* Leyenda */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Leyenda</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#0ea5e9' }}></div>
            <span className="text-sm text-gray-700">Licitación Disponible</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#a855f7' }}></div>
            <span className="text-sm text-gray-700">Licitación En Revision</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#2563eb' }}></div>
            <span className="text-sm text-gray-700">Postulación En Revision</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: '#ea580c' }}></div>
            <span className="text-sm text-gray-700">Postulación Ofertada</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header del calendario */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          
          <h2 className="text-xl font-semibold text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Días de la semana */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {dayNames.map((day) => (
            <div key={day} className="p-4 text-center text-sm font-medium text-gray-500 bg-gray-50">
              {day}
            </div>
          ))}
        </div>

        {/* Días del calendario */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const dayTenders = getFilteredTendersForDate(day.date);
            const dayApplications = getFilteredApplicationsForDate(day.date);
            const allEvents = [
              ...dayTenders.map(tender => ({ type: 'tender' as const, data: tender, status: tender.flujo })),
              ...dayApplications.map(app => ({ 
                type: 'application' as const, 
                data: app, 
                status: applicationStatuses.find(s => s.id === app.status_id)?.name || 'Desconocido'
              }))
            ];
            const hasEvents = allEvents.length > 0;
            const isCurrentDay = isToday(day.date);
            const isPast = isPastDate(day.date);

            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 border-r border-b border-gray-200 ${
                  !day.isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                } ${isCurrentDay ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm font-medium ${
                      !day.isCurrentMonth
                        ? 'text-gray-400'
                        : isCurrentDay
                        ? 'text-blue-600'
                        : isPast
                        ? 'text-gray-500'
                        : 'text-gray-900'
                    }`}
                  >
                    {day.date.getDate()}
                  </span>
                  {hasEvents && (
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 text-orange-500" />
                      <span className="text-xs text-orange-600 ml-1">{allEvents.length}</span>
                    </div>
                  )}
                </div>

                {/* Eventos del día */}
                <div className="space-y-1">
                  {allEvents.slice(0, 3).map((event, eventIndex) => (
                    <div
                      key={`${event.type}-${event.data.id}-${eventIndex}`}
                      className="group relative cursor-pointer"
                      onClick={() => {
                        if (event.type === 'tender') {
                          handleTenderClick(event.data as Tender);
                        } else {
                          handleApplicationClick(event.data as Application);
                        }
                      }}
                    >
                      <div
                        className={`text-xs p-1 rounded text-white transition-all ${
                          isPast ? 'opacity-60' : ''
                        } hover:opacity-80 hover:scale-105 cursor-pointer`}
                        style={{ backgroundColor: getEventColor(event.type, event.status || 'Desconocido') }}
                        title={`${event.type === 'tender' ? 'Licitación' : 'Postulación'}: ${event.type === 'tender' ? (event.data as Tender).title : getTenderInfo((event.data as Application).tender_id)?.title || 'Licitación no encontrada'}`}
                      >
                        <div className="flex items-center">
                          {event.type === 'tender' ? (
                            <FileText className="h-3 w-3 mr-1" />
                          ) : (
                            <Target className="h-3 w-3 mr-1" />
                          )}
                          <div className="truncate font-medium">
                            {event.type === 'tender' 
                              ? (event.data as Tender).tender_code
                              : `P-${(event.data as Application).id.slice(0, 8)}`
                            }
                          </div>
                        </div>
                        <div className="truncate">
                          {event.type === 'tender' 
                            ? (event.data as Tender).title
                            : getTenderInfo((event.data as Application).tender_id)?.title || 'Licitación no encontrada'
                          }
                        </div>
                      </div>

                      {/* Tooltip con información detallada */}
                      <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg -top-2 left-full ml-2 w-64 pointer-events-none">
                        <div className="space-y-2">
                          <div className="font-semibold">
                            {event.type === 'tender' 
                              ? (event.data as Tender).title
                              : getTenderInfo((event.data as Application).tender_id)?.title || 'Licitación no encontrada'
                            }
                          </div>
                          <div className="flex items-center text-gray-300">
                            <Building2 className="h-3 w-3 mr-1" />
                            {event.type === 'tender' 
                              ? getOrganizationName((event.data as Tender).organization_id as string)
                              : (() => {
                                  const tender = getTenderInfo((event.data as Application).tender_id);
                                  return tender ? getOrganizationName(tender.organization_id as string) : 'Organización no encontrada';
                                })()
                            }
                          </div>
                          <div className="flex items-center text-gray-300">
                            <DollarSign className="h-3 w-3 mr-1" />
                            {event.type === 'tender' 
                              ? formatCurrency((event.data as Tender).amount, (event.data as Tender).currency)
                              : formatCurrency((event.data as Application).amount, (event.data as Application).currency)
                            }
                          </div>
                          <div className="flex items-center text-gray-300">
                            <Clock className="h-3 w-3 mr-1" />
                            {event.type === 'tender' 
                              ? `Cierre: ${new Date((event.data as Tender).closing_date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`
                              : `Enviado: ${new Date((event.data as Application).submission_date).toLocaleDateString('es-ES')}`
                            }
                          </div>
                          <div className="text-blue-300 font-medium mt-2">
                            Clic para ver detalles →
                          </div>
                        </div>
                        {/* Flecha del tooltip */}
                        <div className="absolute top-2 -left-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    </div>
                  ))}
                  
                  {allEvents.length > 3 && (
                    <div className="text-xs text-gray-500 text-center py-1">
                      <button 
                        className="hover:text-blue-600 transition-colors"
                        onClick={() => {
                          // Si hay una función para cambiar de página, ir a licitaciones
                          if (onPageChange) {
                            onPageChange('tenders');
                          }
                        }}
                      >
                        +{allEvents.length - 3} más...
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumen del mes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Resumen del mes - {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-900">Licitaciones Nuevas</p>
                <p className="text-2xl font-bold text-blue-600">
                  {tenders.filter(tender => {
                    const closingDate = new Date(tender.closing_date);
                    return closingDate.getMonth() === currentDate.getMonth() &&
                           closingDate.getFullYear() === currentDate.getFullYear() &&
                           (tender.flujo === 'Disponible' || tender.flujo === 'En Revision');
                  }).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-purple-900">Licitaciones En Revision</p>
                <p className="text-2xl font-bold text-purple-600">
                  {tenders.filter(tender => {
                    const closingDate = new Date(tender.closing_date);
                    return closingDate.getMonth() === currentDate.getMonth() &&
                           closingDate.getFullYear() === currentDate.getFullYear() &&
                           tender.flujo === 'En Revision';
                  }).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-900">Postulaciones En Revision</p>
                                 <p className="text-2xl font-bold text-blue-600">
                   {applications.filter(app => {
                     const tender = tenders.find(t => t.id === app.tender_id);
                     if (!tender) return false;
                     
                     const closingDate = new Date(tender.closing_date);
                     const status = applicationStatuses.find(s => s.id === app.status_id);
                     return closingDate.getMonth() === currentDate.getMonth() &&
                            closingDate.getFullYear() === currentDate.getFullYear() &&
                            status?.name === 'En Revision';
                   }).length}
                 </p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-orange-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-orange-900">Postulaciones Ofertadas</p>
                                 <p className="text-2xl font-bold text-orange-600">
                   {applications.filter(app => {
                     const tender = tenders.find(t => t.id === app.tender_id);
                     if (!tender) return false;
                     
                     const closingDate = new Date(tender.closing_date);
                     const status = applicationStatuses.find(s => s.id === app.status_id);
                     return closingDate.getMonth() === currentDate.getMonth() &&
                            closingDate.getFullYear() === currentDate.getFullYear() &&
                            status?.name === 'Ofertada';
                   }).length}
                 </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenderCalendar;